/* LUVA cart drawer upsell (2026-10-05).
   Renders [data-luva-cu] containers in the cart drawer from the cart JSON that the drawer carries
   ([data-luva-cu-cart], refreshed on every cart re-render) and the catalog in #luva-cu-data.
   - Free-delivery progress (AED 200), ZERUP "3 for AED 49" progress (automatic Buy X Get Y discount;
     amounts come from data.pack).
   - "Add more" row: ZERUP in cart -> other flavours, then gummies; gummies in cart -> ZERUP;
     empty -> curated bestsellers. Order inside each group follows Shopify product recommendations
     (complementary, then related) when the API returns anything.
   - Quick add only for available variants after launch; before launch a disabled "Coming soon" button.
   Exposes window.LuvaCartUpsell.render(cart) for tests with a mocked cart. */
import { CartLinesUpdateEvent } from '@shopify/events';

const DATA_EL = () => document.getElementById('luva-cu-data');
let DATA = null;
const recoCache = new Map();
let mockCart = null;

function data() {
  if (DATA) return DATA;
  const el = DATA_EL();
  if (!el) return null;
  try { DATA = JSON.parse(el.textContent); } catch (e) { DATA = null; }
  return DATA;
}

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const aed = (cents) => {
  const v = Math.max(0, cents) / 100;
  return 'AED ' + (Number.isInteger(v) ? String(v) : v.toFixed(2));
};
const isZerup = (it) => it.product_type === 'ZERUP drink syrup' || /zerup/.test(it.handle || '');
const isGummies = (it) => it.product_type === 'Creatine Gummies' || /gummies/.test(it.handle || '');
const EMPTY = { total_price: 0, item_count: 0, items: [] };

function cartFor(container) {
  if (container.hasAttribute('data-empty')) return EMPTY;
  if (mockCart) return mockCart;
  const el = document.querySelector('#cart-drawer [data-luva-cu-cart]') || document.querySelector('[data-luva-cu-cart]');
  if (!el) return EMPTY;
  try { return JSON.parse(el.textContent); } catch (e) { return EMPTY; }
}

const ICON = {
  truck: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M2.5 6.5h11v9h-11zM13.5 9.5h4l3 3.2v2.8h-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="6.5" cy="17" r="1.8" fill="#fff" stroke="currentColor" stroke-width="1.8"/><circle cx="17" cy="17" r="1.8" fill="#fff" stroke="currentColor" stroke-width="1.8"/></svg>',
  check: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  plus: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>',
  clock: '<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8 4.8V8l2.2 1.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  left: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M10 3.5L5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  right: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M6 3.5L10.5 8 6 12.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  bottle: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M6.5 1.5h3v2l1 1.5v9a1 1 0 01-1 1h-3a1 1 0 01-1-1V5l1-1.5z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>'
};

/* ---------- progress ---------- */
function renderProgress(el, cart) {
  const d = data();
  const th = d ? d.threshold : 20000;
  const total = cart.total_price || 0;
  let html = '';

  if (!cart.item_count) {
    html += '<div class="luva-cu-bar luva-cu-bar--info"><span class="luva-cu-bar__ico">' + ICON.truck + '</span>' +
      '<p class="luva-cu-bar__text"><b>' + luvaT('cart_upsell_free_delivery_from', 'Free UAE delivery from AED {amount}').replace('{amount}', th / 100) + '</b><span>' + luvaT('cart_upsell_delivery_time', 'Delivered in max 12h across the UAE') + '</span></p></div>';
  } else {
    const left = th - total;
    const pct = Math.min(1, total / th);
    const done = left <= 0;
    html += '<div class="luva-cu-bar' + (done ? ' is-done' : '') + '">' +
      '<div class="luva-cu-bar__row"><span class="luva-cu-bar__ico">' + (done ? ICON.check : ICON.truck) + '</span>' +
      '<p class="luva-cu-bar__text">' + (done ? '<b>' + luvaT('cart_upsell_free_delivery_unlocked', 'Free delivery unlocked') + '</b>' : '<b>' + luvaT('cart_upsell_away_from_free', '{amount} away from free delivery').replace('{amount}', aed(left)) + '</b>') +
      '<span>' + (done ? luvaT('cart_upsell_delivery_time', 'Delivered in max 12h across the UAE') : luvaT('cart_upsell_free_from_amount', 'Free from AED {amount} · max 12h UAE delivery').replace('{amount}', th / 100)) + '</span></p></div>' +
      '<div class="luva-cu-track" role="progressbar" aria-label="' + luvaT('cart_upsell_progress_label', 'Progress to free delivery') + '" aria-valuemin="0" aria-valuemax="' + th / 100 + '" aria-valuenow="' + Math.min(total, th) / 100 + '" aria-valuetext="' + (done ? luvaT('cart_upsell_free_delivery_unlocked', 'Free delivery unlocked') : luvaT('cart_upsell_amount_to_go', '{amount} to go').replace('{amount}', aed(left))) + '">' +
      '<i style="--p:' + pct.toFixed(4) + '"></i></div></div>';

    const zItems = cart.items.filter(isZerup);
    const z = zItems.reduce((n, it) => n + it.quantity, 0);
    if (z > 0) {
      const inPack = z % 3 === 0 ? 3 : z % 3;
      const need = 3 - inPack;
      const packs = Math.floor(z / 3);
      const imgs = [];
      zItems.forEach((it) => { for (let i = 0; i < it.quantity; i++) imgs.push(it.image); });
      const cur = imgs.slice(imgs.length - inPack);
      let slots = '';
      for (let i = 0; i < 3; i++) {
        slots += i < inPack
          ? '<span class="luva-cu-slot is-on" style="--i:' + i + '">' + (cur[i] ? '<img src="' + esc(cur[i]) + '" alt="" width="40" height="40" loading="lazy">' : ICON.bottle) + '</span>'
          : '<span class="luva-cu-slot" style="--i:' + i + '">' + ICON.plus + '</span>';
      }
      const pk = (d && d.pack) || { price: 4900, save: 800 };
      const saved = packs * pk.save;
      const fl = need + ' ' + (need > 1 ? luvaT('cart_upsell_more_flavours', 'more flavours') : luvaT('cart_upsell_more_flavour', 'more flavour'));
      let txt;
      if (need === 0) txt = '<b>' + luvaT('cart_upsell_pack_unlocked', '3 for {price} unlocked').replace('{price}', aed(pk.price)) + (packs > 1 ? ' ×' + packs : '') + '</b><span>' + luvaT('cart_upsell_you_save_auto', 'You save {amount} · applied automatically').replace('{amount}', aed(saved)) + '</span>';
      else if (packs > 0) txt = luvaT('cart_upsell_add_for_another', 'Add <b>{flavours}</b> for another 3 for {price}').replace('{flavours}', fl).replace('{price}', aed(pk.price)) + '<span>' + luvaT('cart_upsell_already_saving', 'Already saving {amount} · mix any flavours').replace('{amount}', aed(saved)) + '</span>';
      else txt = luvaT('cart_upsell_add_to_get_pack', 'Add <b>{flavours}</b> to get <b>3 for {price}</b>').replace('{flavours}', fl).replace('{price}', aed(pk.price)) + '<span>' + luvaT('cart_upsell_mix_flavours_single', 'Mix any ZERUP flavours · single bottle AED 19') + '</span>';
      const pill = need === 0 ? luvaT('cart_upsell_amount_saved', '{amount} saved').replace('{amount}', aed(saved)) : luvaT('cart_upsell_save_amount', 'Save {amount}').replace('{amount}', aed(pk.save));
      html += '<div class="luva-cu-pack' + (need === 0 ? ' is-done' : '') + '">' +
        '<div class="luva-cu-pack__slots" aria-hidden="true">' + slots + '</div>' +
        '<p class="luva-cu-bar__text">' + txt + '</p>' +
        '<em class="luva-cu-save">' + pill + '</em></div>';
    }
  }
  el.innerHTML = html;
}

/* ---------- recommendations ---------- */
function orderProducts(cart) {
  const d = data();
  if (!d) return [];
  const inCart = new Set(cart.items.map((i) => i.handle));
  const pool = d.products.filter((p) => !inCart.has(p.handle));
  const hasZ = cart.items.some(isZerup);
  const hasG = cart.items.some(isGummies);
  const byBest = (a, b) => d.best.indexOf(a.handle) - d.best.indexOf(b.handle);
  if (!cart.items.length) return pool.sort(byBest);
  const z = pool.filter((p) => p.kind === 'zerup').sort(byBest);
  const g = pool.filter((p) => p.kind === 'gummies').sort(byBest);
  if (hasZ) return z.concat(g);
  if (hasG) return z.concat(g);
  return pool.sort(byBest);
}

function cardHTML(p, d) {
  const soon = d.soon;
  const canAdd = !soon && p.available;
  const price = (p.from ? luvaT('cart_upsell_from', 'from') + ' ' : '') + aed(p.price);
  const img = p.img
    ? '<img src="' + esc(p.img) + '"' + (p.img2x ? ' srcset="' + esc(p.img) + ' 1x, ' + esc(p.img2x) + ' 2x"' : '') + ' alt="" width="300" height="375" loading="lazy" decoding="async" draggable="false">'
    : '';
  let btn;
  if (canAdd) {
    btn = '<button type="button" class="luva-cu-add" data-luva-cu-add="' + p.variant + '" aria-label="' + luvaT('cart_upsell_add_to_cart', 'Add {product} to cart').replace('{product}', esc(p.brand + ' ' + p.name + (p.variantLabel ? ', ' + p.variantLabel : ''))) + '">' + ICON.plus + '</button>';
  } else if (soon) {
    btn = '<button type="button" class="luva-cu-add is-soon" disabled aria-label="' + luvaT('cart_upsell_coming_soon_launch', '{product}: coming soon, launching {date}').replace('{product}', esc(p.brand + ' ' + p.name)).replace('{date}', esc(d.launch)) + '">' + ICON.clock + '</button>';
  } else {
    btn = '<button type="button" class="luva-cu-add is-soon" disabled aria-label="' + luvaT('cart_upsell_sold_out_label', '{product}: sold out').replace('{product}', esc(p.brand + ' ' + p.name)) + '">' + ICON.clock + '</button>';
  }
  const note = canAdd ? (p.variantLabel ? esc(p.variantLabel) : '') : (soon ? luvaT('cart_upsell_coming_date', 'Coming {date}').replace('{date}', esc(d.launch)) : luvaT('cart_upsell_sold_out', 'Sold out'));
  return '<li class="luva-cu-card luva-cu-card--' + p.kind + '" data-handle="' + esc(p.handle) + '" style="--tint:' + esc(p.tint) + '">' +
    '<a class="luva-cu-card__media" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
    btn +
    '<p class="luva-cu-card__brand">' + esc(p.brand) + '</p>' +
    '<a class="luva-cu-card__name" href="' + esc(p.url) + '">' + esc(p.name) + '</a>' +
    '<p class="luva-cu-card__price">' + price + (note ? '<span class="' + (canAdd ? '' : 'is-soon') + '">' + note + '</span>' : '') + '</p>' +
    '</li>';
}

function updateArrows(sec) {
  const list = sec.querySelector('.luva-cu-recs__list');
  const prev = sec.querySelector('[data-luva-cu-prev]');
  const next = sec.querySelector('[data-luva-cu-next]');
  if (!list || !prev) return;
  const max = list.scrollWidth - list.clientWidth - 2;
  prev.disabled = list.scrollLeft <= 2;
  next.disabled = list.scrollLeft >= max;
  sec.classList.toggle('is-scrollable', max > 2);
}

function renderRecs(sec, cart) {
  const d = data();
  const rail = sec.querySelector('.luva-cu-recs__rail');
  if (!d || !rail) return;
  const items = orderProducts(cart).slice(0, 10);
  if (!items.length) { sec.hidden = true; return; }
  sec.hidden = false;
  const head = sec.querySelector('.luva-cu-recs__head');
  const title = sec.querySelector('.luva-cu-recs__title');
  if (title) title.textContent = cart.items.length ? luvaT('cart_upsell_add_more', 'Add more') : luvaT('cart_upsell_popular_picks', 'Popular picks');
  if (head && !head.querySelector('[data-luva-cu-prev]')) {
    head.insertAdjacentHTML('beforeend', '<div class="luva-cu-recs__nav"><button type="button" class="luva-cu-arrow" data-luva-cu-prev aria-label="' + luvaT('cart_upsell_previous_products', 'Previous products') + '">' + ICON.left + '</button><button type="button" class="luva-cu-arrow" data-luva-cu-next aria-label="' + luvaT('cart_upsell_next_products', 'Next products') + '">' + ICON.right + '</button></div>');
  }
  rail.innerHTML = '<ul class="luva-cu-recs__list" role="list" tabindex="0" aria-label="' + (cart.items.length ? luvaT('cart_upsell_add_more_products_scroll', 'Add more products, scroll sideways') : luvaT('cart_upsell_popular_products_scroll', 'Popular products, scroll sideways')) + '">' + items.map((p) => cardHTML(p, d)).join('') + '</ul>' +
    '<p class="luva-cu-recs__status visually-hidden" role="status" aria-live="polite"></p>';
  const list = rail.querySelector('.luva-cu-recs__list');
  list.addEventListener('scroll', () => updateArrows(sec), { passive: true });
  requestAnimationFrame(() => updateArrows(sec));
  if (cart.items.length) applyRecommendations(sec, cart);
}

async function fetchReco(productId, intent) {
  const d = data();
  const key = productId + ':' + intent;
  if (recoCache.has(key)) return recoCache.get(key);
  const p = fetch((d.root || '/').replace(/\/?$/, '/') + 'recommendations/products.json?product_id=' + productId + '&intent=' + intent + '&limit=10')
    .then((r) => (r.ok ? r.json() : { products: [] }))
    .then((j) => (j.products || []).map((x) => x.handle))
    .catch(() => []);
  recoCache.set(key, p);
  return p;
}

async function applyRecommendations(sec, cart) {
  const first = cart.items[0];
  if (!first || !first.product_id) return;
  let handles = await fetchReco(first.product_id, 'complementary');
  if (!handles.length) handles = await fetchReco(first.product_id, 'related');
  if (!handles.length) return;
  const list = sec.querySelector('.luva-cu-recs__list');
  if (!list) return;
  const cards = Array.from(list.children);
  const rank = (li) => { const i = handles.indexOf(li.dataset.handle); return i < 0 ? 999 : i; };
  const kind = (li) => (li.classList.contains('luva-cu-card--zerup') ? 0 : 1);
  const groupFirst = cart.items.some(isZerup) || cart.items.some(isGummies);
  const sorted = cards.slice().sort((a, b) => (groupFirst ? kind(a) - kind(b) : 0) || rank(a) - rank(b) || cards.indexOf(a) - cards.indexOf(b));
  if (sorted.every((li, i) => li === cards[i])) return;
  sorted.forEach((li) => list.appendChild(li));
  sec.setAttribute('data-reco', 'shopify');
}

/* ---------- quick add (only reachable when a variant is available after launch) ---------- */
async function quickAdd(btn) {
  const d = data();
  if (!d || d.soon || btn.disabled) return;
  const id = Number(btn.getAttribute('data-luva-cu-add'));
  if (!id) return;
  const sec = btn.closest('[data-luva-cu]');
  const status = sec && sec.querySelector('.luva-cu-recs__status');
  btn.disabled = true;
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((c) => c.dataset.sectionId).filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  btn.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(id), quantity: 1 }],
    promise: deferred.promise
  }));

  const root = (d.root || '/').replace(/\/?$/, '/');
  try {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: sectionIds.join(','), sections_url: window.location.pathname })
    });
    const json = await res.json();
    const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const failed = !res.ok || !!json.status;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'luva-cart-upsell', itemCount: 1, sections: failed ? undefined : json.sections, didError: failed }
    });
    if (status) status.textContent = failed ? (json.description || json.message || luvaT('cart_upsell_could_not_add', 'Could not add this item.')) : luvaT('cart_upsell_added_to_cart', 'Added to cart.');
    if (failed) { btn.disabled = false; btn.classList.remove('is-busy'); btn.removeAttribute('aria-busy'); }
  } catch (e) {
    deferred.reject(e);
    btn.disabled = false;
    btn.classList.remove('is-busy');
    btn.removeAttribute('aria-busy');
    if (status) status.textContent = luvaT('cart_upsell_could_not_add_retry', 'Could not add this item. Please try again.');
  }
}

/* ---------- wiring ---------- */
function renderAll(force) {
  if (!data()) return;
  document.querySelectorAll('[data-luva-cu]').forEach((el) => {
    if (!force && el.hasAttribute('data-ready')) return;
    const cart = cartFor(el);
    if (el.getAttribute('data-luva-cu') === 'progress') renderProgress(el, cart);
    else renderRecs(el, cart);
    el.setAttribute('data-ready', '');
  });
}

document.addEventListener('click', (e) => {
  const add = e.target.closest('[data-luva-cu-add]');
  if (add) { e.preventDefault(); quickAdd(add); return; }
  const nav = e.target.closest('[data-luva-cu-prev], [data-luva-cu-next]');
  if (nav) {
    const sec = nav.closest('[data-luva-cu]');
    const list = sec && sec.querySelector('.luva-cu-recs__list');
    if (!list) return;
    const card = list.querySelector('.luva-cu-card');
    const step = card ? (card.getBoundingClientRect().width + 10) * 2 : list.clientWidth * 0.8;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({ left: nav.hasAttribute('data-luva-cu-prev') ? -step : step, behavior: reduce ? 'auto' : 'smooth' });
  }
});

let queued = false;
const mo = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  queueMicrotask(() => { queued = false; renderAll(false); });
});

function boot() {
  renderAll(false);
  mo.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', () => document.querySelectorAll('[data-luva-cu="recs"]').forEach(updateArrows));
  document.addEventListener('toggle', () => document.querySelectorAll('[data-luva-cu="recs"]').forEach(updateArrows), true);
}

window.LuvaCartUpsell = {
  render(cart) { mockCart = cart || null; renderAll(true); },
  data
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
